import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { leaveApi } from '../../api/leave';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { ReturnAction } from '../../components/ReturnAction';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { LEAVE_REQUEST_STAGES } from '../../config/workflow-stages';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { useToast } from '../../components/Toast';
import { usePermission } from '../../auth/AuthContext';
import { formatDate } from '../../utils/formatDate';

export function LeaveRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const requestId = Number(id);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('leave.manage');

  const requestQuery = useQuery({ queryKey: ['leave-request', requestId], queryFn: () => leaveApi.getById(requestId) });
  const docsQuery = useQuery({ queryKey: ['leave-request-documents', requestId], queryFn: () => genericDocumentApi.listFor('leave_request', requestId) });

  const decideMutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => leaveApi.hrDecide(requestId, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); queryClient.invalidateQueries({ queryKey: ['leave-request', requestId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision.'),
  });

  if (!requestQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const r = requestQuery.data.data;

  return (
    <div className="page">
      <Link to="/hr/leave-review">&larr; Back to Leave - HR Confirmation</Link>
      <RecordHeader
        title={`Leave Request - ${r.employee_name ?? ''}`}
        entityType="leave_request"
        entityId={requestId}
        fields={[
          { label: 'Leave type', value: r.leave_type_name },
          { label: 'Dates', value: `${formatDate(r.start_date)} - ${formatDate(r.end_date)}` },
          { label: 'Days requested', value: String(r.days_requested) },
          { label: 'Supervisor decision', value: r.supervisor_decision ?? '-' },
        ]}
        actions={
          <span className="row-actions">
            <button className="btn-primary" onClick={() => decideMutation.mutate('approved')} disabled={decideMutation.isPending}><Check size={16} className="inline-icon" /> Confirm</button>
            <button className="button-danger" onClick={() => decideMutation.mutate('rejected')} disabled={decideMutation.isPending}><X size={16} className="inline-icon" /> Reject</button>
            <ReturnAction onReturn={(comment) => leaveApi.hrReturn(requestId, comment)} queryKeysToInvalidate={[['leave-request', requestId]]} />
          </span>
        }
        readOnly={!canManage}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: (
              <>
                <section className="card">
                  <h3>Reason</h3>
                  <p>{r.reason}</p>
                  {r.return_comment && <p className="muted">Return comment: {r.return_comment}</p>}
                </section>
                <CommentsThread entityType="leave_request" entityId={requestId} />
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('leave_request', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('leave_request', requestId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('leave_request', documentId)}
                queryKeyToInvalidate={['leave-request-documents', requestId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="leave_request" entityId={requestId} actionStageMap={LEAVE_REQUEST_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
